/**
 * 데스크톱 드롭다운과 모바일 drawer 가 같은 항목 모델을 쓴다 — 활성/비활성·사유·순서의 단일 정의.
 * 표시 정보는 QUESTION_CARD_AI_MENU_ENTRIES, 자격 판정은 QUESTION_CARD_AI_ACTION_SPECS 의 eligibility.
 * 실행 직전 재검사도 같은 함수를 쓰므로 "메뉴에선 활성인데 실행에서 막힘" 이 생기지 않는다.
 */
import { useWatch } from 'react-hook-form';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { trackEvent } from '@zep/logger/tracking';
import { useQuizList } from '../../../components/useQuizList';
import { useBuildFormContext } from '../../../form/schema';
import { QUESTION_CARD_AI_MENU_ENTRIES, type QuestionCardAiMenuEntry } from '../consts/questionCardAiMenu.const';
import { type QuestionCardAiMenuItem } from '../types/questionCardAi.types';
import { type QuestionCardAiMenuPlace } from '../utils/events.quiz-question-card-ai';
import { QUESTION_CARD_AI_ACTION_SPECS } from '../utils/questionCardAiActionSpecs';
import { isQuestionCardAiQuizType } from '../utils/questionCardAiEligibility';
import { eligibilityReasonMessage } from '../utils/questionCardAiErrorMessage';
import { fillSlotVariantOf } from '../utils/questionCardAiSlotSchema';
import { useQuestionCardAiActions } from './useQuestionCardAiActions';
import { useQuestionCardAiCopy } from './useQuestionCardAiCopy';

/**
 * AI 항목이 없으면 진입 이벤트를 남기지 않는다. 모바일 drawer 는 복제·삭제 같은 일반 도구와
 * 함께 열리므로, 항목 없는 유형(짝맞추기·순서맞추기)의 drawer 열림이 AI 메뉴 진입으로 잡힌다.
 */
const NO_TRACK = () => undefined;
export const useQuestionCardAiMenuItems = ({
  quizId,
  quizIndex,
  place
}: {
  quizId: string;
  quizIndex: number;
  /** 이 메뉴를 그리는 표면 — 진입·클릭 이벤트가 데스크톱/모바일을 구분한다. */
  place: QuestionCardAiMenuPlace;
}) => {
  const copy = useQuestionCardAiCopy();
  const {
    control
  } = useBuildFormContext();
  // 카드마다 quizList 전체를 구독하면 한 글자 입력에도 전 카드가 리렌더돼
  // EditableQuizCardWithProvider 메모이제이션이 무력해진다 — 해당 문항만 구독한다.
  const quiz = useWatch({
    control,
    name: `quizList.${quizIndex}`
  });
  const {
    quizList
  } = useQuizList();
  const {
    run,
    openChat,
    isLocked
  } = useQuestionCardAiActions({
    quizId
  });
  if (!quiz || quiz.id !== quizId) return {
    items: [],
    isLocked,
    quizType: '',
    trackMenuOpen: NO_TRACK
  };
  if (!isQuestionCardAiQuizType(quiz.type)) return {
    items: [],
    isLocked,
    quizType: quiz.type,
    trackMenuOpen: NO_TRACK
  };
  const lockedReason = isLocked ? copy.reason.running : undefined;
  const toItem = (entry: QuestionCardAiMenuEntry): QuestionCardAiMenuItem | null => {
    const {
      key
    } = entry;
    const base = {
      key,
      group: entry.group,
      label: copy.menu[entry.labelKeyByType?.[quiz.type] ?? entry.labelKey]
    };
    // 비활성 항목은 라벨(흐림)과 아이콘 색을 함께 맞춘다.
    const iconOf = (disabled: boolean) => <entry.Icon size="sm" fill={disabled ? ICON_COLOR.disabled : ICON_COLOR.default} data-sentry-element="unknown" data-sentry-component="iconOf" data-sentry-source-file="useQuestionCardAiMenuItems.tsx" />;
    if (key === 'chat') {
      if (!openChat) return null;
      // 빠른 액션이 도는 중에 채팅이 문항을 갈아끼우면, 먼저 나간 요청의 결과가
      // 바뀐 문항에 적용된다(응답에 리비전 검사가 없다). 진입을 함께 막는다.
      return {
        ...base,
        icon: iconOf(isLocked),
        disabled: isLocked,
        reason: lockedReason,
        onSelect: () => openChat(place)
      };
    }
    const eligibility = QUESTION_CARD_AI_ACTION_SPECS[key].eligibility({
      quiz,
      count: quizList.length
    });
    // 지원하지 않는 유형은 사용자가 어떻게 해도 풀 수 없는 상태라 사유를 안내하지 않고 항목을 뺀다.
    if (!eligibility.enabled && (entry.hideWhenIneligible || eligibility.reason === 'unsupportedType')) return null;
    const reason = eligibility.enabled ? undefined : eligibilityReasonMessage(copy, eligibility.reason, fillSlotVariantOf(quiz.type));
    const disabled = isLocked || !eligibility.enabled;
    return {
      ...base,
      icon: iconOf(disabled),
      disabled,
      reason: lockedReason ?? reason,
      onSelect: () => run(key, place)
    };
  };
  const items = QUESTION_CARD_AI_MENU_ENTRIES.filter(entry => entry.visible !== false).map(toItem).filter((item): item is QuestionCardAiMenuItem => item !== null);

  // 데스크톱 드롭다운과 모바일 drawer 가 같은 진입 이벤트를 쓰고 place 로만 갈린다.
  const trackMenuOpen = () => trackEvent('quiz_question_card_ai_menu_open', {
    // useFieldArray().fields 스냅샷은 중첩 setValue(유형 변경)를 반영하지 않는다.
    // 위에서 이미 구독 중인 값을 쓴다.
    quiz_type: quiz.type,
    question_index: quizIndex,
    place,
    enabled_actions: items.filter(item => !item.disabled).map(item => item.key),
    is_locked: isLocked
  });
  return {
    items,
    isLocked,
    quizType: quiz.type,
    trackMenuOpen: items.length > 0 ? trackMenuOpen : NO_TRACK
  };
};